// i18n area 'protection' (M4b, 2026-08-04 — snapshot-based backups with
// admin-proof immutability + asynchronous DR replication, contract
// backups.yaml/replication.yaml): the Storage ▸ Data Protection pages —
// Backup Targets, Backups, Replication. The IMMUTABILITY copy is deliberately
// HONEST (no overclaim, the design decision of the round): governance is
// OUR-API-enforced, compliance delegates to the destination's own Object
// Lock, and the future-only rule is stated wherever policy is edited.
// Top-level key `protection` — dictionaries.ts composes sections with a
// SHALLOW spread, so nesting under `storage` here would clobber storage.ts.

export const en = {
  protection: {
    targets: {
      title: 'Backup targets',
      createTitle: 'Create backup target',

      // SEC-WORM-1 (2026-09-06): the tape kind (a destination of COPIES) and
      // the byte-scrub policy.
      tapeHint:
        'A tape target takes COPIES of ready backups (backup.archive), never backups: the drive is attached to ONE node, the cartridge is appended (tar + index on the cartridge) and never rewritten — a WORM medium refuses the write.',
      tapeNode: 'Node of the drive',
      tapeNodeHint: 'The cluster node the tape drive is attached to',
      tapeDrive: 'Drive (no-rewind device)',
      tapeChanger: 'Changer (optional)',
      tapeChangerHint: 'Empty: the operator loads the cartridge by hand',
      tapeWorm: 'Medium',
      tapeWormHint: 'required: a cartridge that is not WORM is refused before a byte is written',
      tapeWormRequired: 'WORM required',
      tapeWormAny: 'Any cartridge',
      scrubLabel: 'Byte scrub',
      scrubHint:
        'Read every backup back in full and check its sha256 against meta.json — "the bytes are still those", a different sentence from "the stamp still verifies"',
      scrubEveryDays: 'Every (days)',
      scrubWindowHours: 'Window (hours)',
      scrubEvery: 'every {days} d, {hours} h window',
      scrubOff: 'off',
      editTitle: 'Edit backup target {name}',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes',
      kind: 'Kind',
      kinds: {
        local: 'Local path',
        s3: 'S3',
        tape: 'Tape (WORM copies)'
      },
      kindHints: {
        local:
          'A directory on the executing node — for rigs and small installs; its bytes are only as safe as that filesystem',
        s3: 'Any S3-compatible object store (AWS, MinIO, Ceph RGW…) — the real destination kind, and the only one that can carry compliance mode',
        tape: 'A tape drive on one node — a destination of COPIES (archive a ready backup onto a WORM cartridge), never of backups'
      },
      kindImmutableHint: 'The kind is fixed at creation — retyping a destination is a NEW target',
      path: 'Path',
      pathHint: 'Absolute directory path, writable on every node that may run backup tasks',
      endpoint: 'Endpoint URL',
      endpointHint: 'Always explicit, e.g. https://minio.example:9000 — no provider guessing',
      s3Bucket: 'Bucket',
      s3BucketHint: 'Compliance mode requires Object Lock enabled on the bucket (fixed at S3 bucket creation)',
      region: 'Region',
      regionHint: 'Optional signing region — S3-compatible stores often ignore it',
      prefix: 'Key prefix',
      prefixHint: 'Optional — prepended to every object this target writes',
      forcePathStyle: 'Path-style addressing',
      forcePathStyleHint:
        'endpoint/bucket/key instead of virtual-hosted — the MinIO-friendly default; AWS accepts both',
      credential: 'S3 credential',
      credentialHint:
        'A keystore entry of kind “S3 account” — the secret key is resolved by the worker at use time, never rendered',
      noCredentials: 'No S3 account credential in the keystore yet — create one on the Credentials page first.',
      immutabilityTitle: 'Immutability policy',
      mode: 'Immutability mode',
      modes: {
        off: 'Off',
        governance: 'Governance',
        compliance: 'Compliance'
      },
      modeHints: {
        off: 'Retention is advisory: a window may still be stamped and swept, but deletes work at any time',
        governance:
          'TVStor refuses deletion before the window ends — for every principal, admins and sudo included; no force path exists',
        compliance: 'Governance PLUS the destination enforces the same window itself (S3 Object Lock, compliance mode)'
      },
      modeWarning:
        'Honest boundary — Governance is enforced by the TVStor API alone: no endpoint, flag or force path can delete a protected backup, for any principal including admins; someone holding the destination’s raw credentials (filesystem root, the S3 keys) can however still destroy the bytes outside TVStor. Compliance additionally stamps S3 Object Lock with the same date, so the destination itself refuses — even to the storage credentials.',
      futureOnlyRule:
        'Policy changes speak only to FUTURE backups: every backup freezes its retention window and mode on its own row at creation, so turning immutability off or lowering the window NEVER weakens anything already taken.',
      complianceNeedsS3:
        'Compliance requires an S3 target on an Object-Lock-enabled bucket — a filesystem cannot make that promise',
      retentionDays: 'Retention window (days)',
      retentionDaysHint: 'Stamped on each future backup at creation: retain-until = creation time + window',
      retentionDaysRequired: 'Governance and compliance require a window — an immutability promise needs one',
      retentionDaysOptional:
        'Optional with mode Off: present = advisory window, absent = backups live until deleted by hand',
      backupCount: 'Backups',
      noBackups: 'None yet',
      deleteRetainedHint:
        'A target with governance/compliance backups inside their window cannot be removed until the window passes — that is the promise working as designed.',

      // #181 ondata 2 (SEC-TSA #60/#180, contract BackupTarget.timestamp):
      // the target's RFC 3161 authority — column + detail (ondata 2, read)
      // and the create/edit form (ondata 3: the CLI/API fence fell).
      tsa: {
        label: 'Timestamping (TSA)',
        none: 'No authority',
        noneHint:
          'Declared absence, not a degraded mode: no RFC 3161 authority is configured, so future backups carry state “none” — nobody was asked and nothing failed',
        configured: 'TSA configured',
        verifyEveryShort: 're-read every {days} d',
        verifyOffShort: 'periodic check off',
        url: 'Authority endpoint',
        verifyEvery: 'Periodic verification',
        verifyEveryDays:
          'Every {days} days the pass re-fetches token, metadata and LTV bundle (~10 KiB per backup, never the payload) and re-runs the public verify recipe — a stamp that rots is demoted honestly on its own row instead of staying a green that lies',
        verifyOff: 'Off (0 days) — the operator’s declared choice',
        policyOid: 'Policy OID',
        policyDefault: 'Authority default — accepted and recorded on each backup',
        hashAlg: 'Imprint digest',
        hashAlgs: {
          sha256: 'SHA-256',
          sha384: 'SHA-384',
          sha512: 'SHA-512'
        },
        qualified: 'Qualified (eIDAS)',
        qualifiedYes:
          'Declared QUALIFIED by the operator — never inferred and never verified by TVStor: the product repeats the declaration, it does not grant the presumption',
        qualifiedNo:
          'Not declared qualified — an RFC 3161 token has no legal presumption of its own: the eIDAS presumption holds only for qualified stamps',
        pin: 'Endpoint TLS pin',
        pinNone:
          'None — the endpoint is plain http: in RFC 3161 the security is the token’s signature, not the transport, and only a hash travels',
        anchor: 'Trust anchor',
        anchorPresent:
          'PEM provided ({chars} characters) — what tokens are verified against here, and what an operator hands to a third party with the token',

        // #181 ondata 3: the anchor leaves the detail on demand — it is the
        // material an operator hands to a third party with the token
        // (ratifica 2026-08-19 §7: copy/download arrives with the TSA verbs).
        copyAnchor: 'Copy PEM',
        copiedAnchor: 'Copied',
        downloadAnchor: 'Download PEM',

        // #181 ondata 3 (SEC-TSA #60/#180): the WRITE half — the create/edit
        // form of the `timestamp` block. Every hint mirrors the contract's own
        // sentence; the server admission stays the referee.
        declare: 'Declare an RFC 3161 authority',
        declareHint:
          'Off is a DECLARED absence, not a degraded mode: future backups carry state “none” — nobody was asked and nothing failed',
        forwardOnly:
          'Like immutability, this is a POLICY and speaks only FORWARD: what a backup obtained is frozen on its own row, so pointing the target at another authority never rewrites who signed a backup taken two years ago.',
        clearWarning:
          'Removing the authority stops stamping FUTURE backups (their state will read “none”); already-stamped rows keep their frozen report, and the periodic verification pass ends with the declaration.',
        urlHint:
          'The TSP endpoint. Plain http: is the ORDINARY choice — in RFC 3161 the security is the token’s signature, not the transport, and only a hash ever leaves the cluster; with https: the TLS pin below becomes required',
        anchorFieldHint:
          'REQUIRED, in PEM — deliberately NOT “the system bundle”: the design measured that the bundle is not enough and changes underfoot. It is also the anchor an operator hands to a third party together with the token',
        anchorNotPem: 'Not a PEM certificate — paste the full block including “-----BEGIN CERTIFICATE-----”',
        pinFormHint:
          'sha-256 of the endpoint’s certificate, 64 hex characters (colons tolerated — shipped normalized). Required with an https: endpoint, refused with http: — an anchor that pins nothing would be worse than the honest absence of TLS',
        pinRequired: 'An https: TSA endpoint is never dialed without its pin — the admission refuses naming this field',
        pinForbidden: 'A plain-http endpoint has no TLS to anchor — clear the pin or switch the URL to https:',
        policyOidHint:
          'Optional, sent with the request: an authority that cannot honour it REFUSES (RFC 3161) — the check is the authority’s own. Empty = the authority’s default policy, accepted and recorded on each backup',
        hashAlgHint: 'The imprint digest — the field exists because in ten years it will not be sha256',
        verifyEveryLabel: 'Re-verify every (days)',
        verifyEveryFormHint:
          'The periodic pass re-reads this target’s stamps every N days (token, metadata and LTV bundle — never the payload). Default 30 and active by itself; 0 switches it off, and that is your declared choice',
        qualifiedDeclare: 'Declared qualified (eIDAS)',
        qualifiedWarning:
          'YOUR declaration, never verified and never inferred by TVStor: the eIDAS presumption holds only for a qualified time stamp issued by a QTSP in the EU Trusted List (service state “granted”). The product repeats what you declare — on records that may end up in court.'
      },
      tabOverview: 'Overview'
    },
    backups: {
      title: 'Backups',
      createTitle: 'Create backup',

      // SEC-WORM-1 (2026-09-06): the byte scrub and the WORM copies.
      scrubLabel: 'Bytes',
      scrubStates: {
        none: 'never scrubbed',
        ok: 'bytes verified',
        failed: 'A BYTE DOES NOT COME BACK',
        unreadable: 'unreadable'
      },
      scrubNow: 'Scrub now',
      scrubbing: 'Reading back…',
      archiveTarget: 'Tape target',
      archiveMedium: 'Cartridge label',
      archiveRun: 'Archive on tape',
      copiesChip: 'WORM ×{count}',
      copiesLabel: 'WORM copies (a copy protects this backup from deletion; declare the medium lost to unblock it)',
      copyPhases: {
        pending: 'queued',
        writing: 'writing',
        verifying: 'reading back',
        ready: 'ready',
        error: 'error'
      },
      mediumStates: {
        loaded: 'in the drive',
        ejected: 'ejected',
        unknown: 'not observed',
        lost: 'LOST'
      },
      volume: 'Volume',
      volumeHint: 'Backups are taken from a snapshot of this volume — never from the live head',
      volumeGone: 'Volume deleted',
      volumeGoneHint: 'The source volume no longer exists — the backup stays restorable into a new volume',
      snapshot: 'Snapshot',
      snapshotAuto: 'Take one now (recommended)',
      snapshotHint:
        'Left on automatic, the platform takes a snapshot first (a child task) and keeps it as the diff base for the next incremental of this volume+target chain',
      target: 'Target',
      targetHint: 'The destination and its immutability policy — the window is stamped and frozen at creation',
      kind: 'Kind',
      kinds: {
        auto: 'Automatic',
        full: 'Full',
        incremental: 'Incremental'
      },
      kindHint:
        'Automatic picks incremental when a usable base exists (the most recent ready backup of this volume on this target), full otherwise; an incremental ships only the changed extents between the base snapshot and the new one',
      phase: 'Phase',
      phases: {
        pending: 'Pending',
        running: 'Running',
        ready: 'Ready',
        error: 'Error',
        expiring: 'Expiring'
      },

      // #181 T9 (SEC-TSA): the five stamp states — only `ok` means
      // "timestamped"; the row's tooltip carries the server-minted claim.
      timestampLabel: 'Timestamp',
      timestampStates: {
        none: 'No authority',
        ok: 'Timestamped',
        unverifiable: 'NOT timestamped — unverifiable',
        unreachable: 'NOT timestamped — authority unreachable',
        refused: 'NOT timestamped — refused'
      },
      timestampNotQualified: 'not eIDAS-qualified',

      // #181 ondata 2 (#180): the full stamp report — a detail tab per backup.
      // Everything here is READ of the frozen row; verifyTimestamp (the verb)
      // is a later wave.
      timestampTab: {
        title: 'Timestamp',
        empty: 'No timestamp report on this row.',
        claim: 'The claim',
        claimHint:
          'The one sentence the product is allowed to say — minted server-side in one place, so webui, CLI and the verification report cannot drift',
        state: 'State',
        authority: 'Authority',
        authorityHint:
          'The endpoint that stamped, or that was asked — a frozen copy of the target’s configuration at ship time',
        stampedAt: 'Time inside the token',
        stampedAtHint:
          'The authority’s own time, never this cluster’s clock — the date that can be held against a third party',
        noToken: 'No token',
        policy: 'TSA policy',
        reason: 'Reason',
        ltv: 'LTV bundle',
        ltvStates: {
          none: 'None',
          partial: 'Partial',
          complete: 'Complete',
          retrofitted: 'Retrofitted'
        },
        ltvHints: {
          none: 'No bundle beside this token yet — true of backups stamped before the LTV feature, until the periodic pass retrofits them',
          partial:
            'The bundle exists but a CRL is missing (a distribution point did not answer) — the reason says which and why',
          complete:
            'Anchor plus one CRL per declared distribution point, frozen at stamp time: the stamp re-verifies OFFLINE, at any future date, with one standard openssl command and no TVStor',
          retrofitted:
            'Built AFTER the stamp with today’s CRLs: it proves non-revocation as of the retrofit date, not as of stamp time — less than complete, and it says so instead of pretending'
        },
        checkedAt: 'Last re-checked',
        checkedAtNever:
          'Never since the stamp — true of every backup that predates the feature and of every backup not yet due',
        checkedAtHint:
          'When the periodic pass (or the verify action) last re-read this stamp — advanced on every visit, green or red; what it found is state and reason',
        observedAt: 'Report written at',
        observedAtHint:
          'By our clock — named apart from the token’s own time precisely because it is not the date that counts',
        offlineRecipe:
          'The token lives beside the backup on the destination (tst.tsr, next to meta.json and data) and is verifiable WITHOUT TVStor by anyone holding the anchor: openssl ts -verify -data meta.json -in tst.tsr -CAfile <anchor>. What it proves: this exact content existed NO LATER than that date. What it does not prove: that the bytes are still there.',
        cadence: 'Periodic verification (target policy)',
        cadenceEvery:
          'Every {days} days — the target’s CURRENT policy: it says when the pass re-reads, never what this row obtained',
        cadenceOff: 'Off (0 days) — the operator’s declared choice on the target',
        cadenceNoAuthority: 'The target declares no authority today — already-stamped rows keep their frozen report',
        cadenceUnknown: 'Target not readable — cadence unknown',

        // #181 ondata 3 (backups.yaml actionVerifyTimestamp): the verify verb.
        // The outcome rendered is ALWAYS the server's — the refreshed row's
        // claim on success, the task's own error verbatim on failure; no green
        // is ever deduced client-side.
        verifyAction: 'Verify now',
        verifyHint:
          'Re-fetches token and metadata from the destination (a few KiB — never the payload) and re-runs the public verify recipe against the anchor frozen on this row. A stamp that no longer verifies is demoted honestly on the row — that is the point, not a damage',
        verifyBlockedNoToken:
          'Nothing to re-read: this row carries no token (the authority was absent, unreachable or refused at ship time) — the server refuses too, and inventing a verification of nothing would be worse',
        verifyRunning: 'Verification task running — the destination is being re-read…',
        verifyFailed: 'The verification FAILED — the server’s own words:',
        verifyReread: 'Report re-read: the page below is the fresh verdict from the row, not a client-side deduction.'
      },
      snapshotProvenanceHint:
        'Provenance: the snapshot this copy was taken from, kept as the chain base — deleting it merely forces the next backup of this chain to be full',
      snapshotNotRecorded: 'Not recorded yet — the platform child task records the snapshot it takes',
      dependents: 'Dependents',
      dependentsNone: 'No incremental depends on this backup',
      dependentsSome:
        '{count} incremental(s) list this backup as base — deletion refuses (409 in_use) until they go: delete leaf-first',

      // #181 ondata 5 (ratifica §7): the whole chain, navigable.
      chain: {
        tabTitle: 'Chain',
        hint: 'The whole chain this backup belongs to, composed from the loaded rows: the full at the root, incrementals indented under their base. Restore materializes the path from the root to the chosen row; delete goes leaf-first (the server refuses a base in use).',
        thisRow: 'this backup',
        missingBase:
          'The base {id} of this chain is not among the loaded rows — the tree below starts from the nearest known ancestor.'
      },
      size: 'Size',
      retainUntil: 'Retain until',
      retention: {
        unprotected: 'No window',
        advisory: 'Advisory',
        locked: 'Locked',
        expired: 'Expired'
      },
      retentionHints: {
        unprotected: 'No retention window on this row — it lives until deleted by hand',
        advisory:
          'The window is advisory (mode Off frozen at creation) — the sweeper reaps past it, but delete works today',
        locked:
          'Frozen governance/compliance inside the window: deletion is refused for every principal until the date passes',
        expired: 'The window has passed — delete works and the expiry sweeper will reap it'
      },
      immutabilityMode: 'Immutability',
      frozenHint:
        'Frozen copy of the target’s policy at creation — this row is judged against it, never against the target’s current policy',
      base: 'Base',
      baseFull: 'Full — no base',
      noVolumes: 'No volume to back up yet.',
      noTargets: 'No backup target yet — create one on the Backup Targets page first.',
      noSnapshots: 'No ready snapshot on this volume — leave automatic to take one now.',
      createFollowHint:
        'Retention is stamped before the first byte moves — the backup is protected from the moment it exists.',
      restore: {
        action: 'Restore',
        title: 'Restore backup of {volume}',
        chainHint:
          'Restore materializes the whole chain — the full, then every incremental up to this backup, in order — read from the destination objects, so it works with the source volume long gone.',
        variant: 'Restore into',
        variantExisting: 'Overwrite an existing volume',
        variantNew: 'A new volume',
        targetVolume: 'Volume to overwrite',
        targetVolumeHint: 'Must be ready or degraded, at least as big as the backup, and not exported',
        overwriteWarning:
          'Destructive: the volume’s contents are replaced with the restored image. Its snapshots survive as revert points.',
        confirmLabel: 'Type the volume name to confirm',
        confirmHint: 'Type “{name}” exactly — its contents are about to be replaced',
        newName: 'New volume name',
        newNameHint: 'Lowercase letters, digits and dashes',
        datapool: 'Cluster',
        datapoolHint: 'Where the new volume is born — defaults to the source volume’s cluster while that volume exists',
        datapoolDefault: 'Source volume’s cluster (default)',
        notReady: 'Only a ready backup can be restored',
        submit: 'Restore'
      },
      deleteTitle: 'Delete backup?',
      deleteWarning: 'The delete task removes the object from the destination and drops the row.',
      deleteLocked:
        'This backup is IMMUTABLE until {date}: its frozen {mode} window refuses deletion for every principal — admins and sudo included. No force flag exists, and disabling immutability on the target only affects future backups.',
      deleteChainHint: 'A backup referenced by an incremental refuses deletion (409 in_use) — delete leaf-first.',
      deleteErrorFreely: 'A failed copy protects nothing — this row deletes freely regardless of retention.',
      tabOverview: 'Overview'
    },
    replication: {
      title: 'Replication links',
      createTitle: 'Create replication link',
      editTitle: 'Edit replication link {name}',
      name: 'Name',
      nameHint: 'Lowercase letters, digits and dashes',
      remoteTitle: 'Remote cluster',
      remoteUrl: 'Backend URL',
      remoteUrlHint: 'Any member node of the destination cluster serves, e.g. https://node1.dr.example:8443',
      clusterName: 'Expected cluster name',
      clusterNameHint:
        'Verified against the receive endpoint’s answer on every sync — data never ships to a cluster answering with a different identity',
      credential: 'Replication token',
      credentialHint:
        'A keystore entry of kind “Replication token”, minted by the DESTINATION operator — presented as the bearer on the remote’s internal receive plane, never a user session',
      noCredentials:
        'No replication token in the keystore yet — the destination operator mints one; store it on the Credentials page first.',

      // #181 T7 (#89 TLS-PIN): the link's TLS anchor — without it an https
      // destination is never dialed and every ship refuses naming the field.
      certFingerprint: 'Destination certificate fingerprint (TLS anchor)',
      certFingerprintHint:
        "sha-256 of the destination backend's TLS certificate, 64 hex characters (colons tolerated) — the destination operator reads it beside the tvsl_ token. Ignored on a plain-http URL.",
      anchorRequired:
        'An https destination is never dialed without its certificate fingerprint — every sync would refuse naming this field. Paste the anchor the destination operator gave you with the token.',
      trustBoundary:
        'The token authorizes exactly one thing on the destination: pushing delta streams into its replica volumes. It is not a user and holds no other permission.',
      volumes: 'Volumes',
      volumesHint:
        'Adding a volume starts with a full seed on the next sync; removing one stops shipping but never touches the destination — its replica remains.',
      volumesEmpty: 'No eligible volume — replica volumes cannot be replicated onward.',
      volumeCount: '{count} volumes',
      schedule: 'Schedule',
      scheduleManual: 'Manual only (Sync now)',
      everyMinutes: 'Sync every (minutes)',
      everyMinutesHint: 'A slow sync self-throttles: overlapping syncs of one link never happen',
      enabled: 'Enabled',
      enabledHint: 'Pausing keeps watermarks and remote replicas; re-enabling resumes incrementally from the kept base',
      pausedChip: 'Paused',
      phase: 'Phase',
      phases: {
        idle: 'Idle',
        syncing: 'Syncing',
        error: 'Error',
        promoted: 'Promoted'
      },
      lastSyncedAt: 'Last synced',
      neverSynced: 'Never',

      // #181 ondata 2: `direction` rendered (census: typed by the client,
      // thrown away by the UI). One value today — declared so the field does
      // not surprise anyone when a pairing round adds `inbound`.
      directionLabel: 'Direction',
      directions: {
        outbound: 'Outbound'
      },
      directionHint:
        'A link lives on the SOURCE cluster and pushes. The destination has no link object — its half is the token-authenticated receive endpoint plus the read-only replica volumes it materializes.',
      lag: 'Lag',
      lagHint:
        'Seconds since the oldest per-volume sync — the observed upper bound on data loss if the source site died now (RPO observed, not promised)',
      remoteReadOnly:
        'Remote replicas are read-only: they refuse exports, writes and revert. Promoting a DR copy to primary is a later round — deleting a link never touches them.',
      syncNow: {
        action: 'Sync now',
        title: 'Sync {name} now',
        hint: 'Runs the same sync cycle the schedule would, off-schedule — e.g. right before planned source-site maintenance. One child per volume: snapshot, diff, ship, commit.',
        blockedDisabled: 'The link is paused — enable it first',
        blockedSyncing: 'A sync is already running — never two concurrent syncs of one link',
        submit: 'Sync now'
      },
      volumeStates: {
        tabTitle: 'Volumes',
        empty: 'No per-volume watermark yet — the first sync creates them.',
        volume: 'Volume',
        baseSnapshot: 'Base snapshot',
        noBase: 'Full seed on next sync',
        lastSyncedAt: 'Last synced',
        lastError: 'Last error',
        ok: 'OK'
      },
      deleteWarning:
        'Deleting the link cancels a running sync and garbage-collects local replication snapshots — the destination is never touched: its replicas remain.',
      tabOverview: 'Overview',

      // ENEA-A-R9 «replica file» (2026-10-23): the FILE form of a link —
      // CephFS shares mirrored by cephfs-mirror, one peer per filesystem.
      kind: 'Form',
      kinds: {
        block: 'Block',
        file: 'File (CephFS mirror)'
      },
      kindHint:
        'Block: volumes ship delta streams. File: CephFS shares are mirrored by cephfs-mirror, snapshot by snapshot — decided at birth, immutable.',
      fileFormHint:
        'ONE PEER PER FILESYSTEM (the star): every share of a CephFS filesystem rides the same link — a second link on a mirrored filesystem is refused. The peer’s mons, MDS and OSDs must be reachable from this cluster’s ceph control node, and BOTH clusters must speak msgr2 secure (the bind refuses otherwise, speaking). The peering itself rides this link’s token and TLS anchor.',
      members: 'Members',
      shares: 'Shares',
      sharesHint:
        'CephFS shares, ready, not themselves replicas. Adding one registers its subvolume with the mirror (the next cadence takes its first snapshot — a full copy on the peer); removing one unregisters it and leaves the peer’s replica share as-is.',
      sharesEmpty: 'No eligible share — a file link mirrors ready CephFS shares that are not replicas.',
      shareCount: '{count} shares',
      cadence: 'Snapshot cadence',
      fileCadenceHint:
        'One platform snapshot per share per tick; the daemon ships every snapshot it finds. Empty = manual only (Sync now).',
      retainSnapshots: 'Snapshots to retain',
      retainSnapshotsHint:
        'How many cadence snapshots stay on each share — and, since cephfs-mirror propagates deletions, on the peer. The newest and the last synced are never pruned.',
      retainSnapshotsDetail:
        'Retention: {count} cadence snapshots per share (the peer keeps the same — deletions propagate).',
      whatTravels:
        'What reaches the peer: the DATA of the snapshots (and the bare Ceph snapshots themselves, the user ones too). What does NOT: the snapshot resources, their names and their frozen retention, the quota (re-applied at promotion). Promotion is a verb on the DESTINATION share; no automatic failback — the reverse protection is a new link from the promoted cluster.',
      mirrorTitle: 'Mirror',
      peerPhases: {
        pending: 'pending',
        bound: 'bound',
        error: 'error',
        unbinding: 'unbinding'
      },
      daemonUp: 'daemon up on {node}',
      daemonDown: 'daemon DOWN on {node}',
      daemonUnknown: 'daemon not observed yet',
      shareStates: {
        tabTitle: 'Shares',
        blockLink: 'A block link has no share states — see the Volumes tab.',
        empty: 'No share state yet — the control node observes the mirror daemon on its next tick.',
        share: 'Share',
        state: 'State',
        states: {
          unknown: 'Unknown',
          idle: 'Idle',
          syncing: 'Syncing',
          failed: 'Failed'
        },
        lastSnapshot: 'Last snapshot',
        lastSyncedSnapshot: 'Last synced',
        lag: 'RPO',
        failureReason: 'Failure'
      }
    }
  }
}

export const it: typeof en = {
  protection: {
    targets: {
      title: 'Destinazioni backup',
      createTitle: 'Crea destinazione backup',
      tapeHint:
        'Una destinazione a nastro prende COPIE di backup pronti (backup.archive), mai backup: il drive è di UN nodo, la cartuccia si scrive in append (tar + indice sulla cartuccia) e mai si riscrive — un supporto WORM rifiuta la scrittura.',
      tapeNode: 'Nodo del drive',
      tapeNodeHint: 'Il nodo del cluster a cui è collegato il drive a nastro',
      tapeDrive: 'Drive (device no-rewind)',
      tapeChanger: 'Changer (facoltativo)',
      tapeChangerHint: "Vuoto: la cartuccia la carica l'operatore",
      tapeWorm: 'Supporto',
      tapeWormHint: 'required: una cartuccia non WORM è rifiutata prima di scrivere un byte',
      tapeWormRequired: 'WORM obbligatorio',
      tapeWormAny: 'Qualsiasi cartuccia',
      scrubLabel: 'Scrub dei byte',
      scrubHint:
        'Rilegge ogni backup per intero e confronta lo sha256 con meta.json — «i byte sono ancora quelli», una frase diversa da «la marca vale ancora»',
      scrubEveryDays: 'Ogni (giorni)',
      scrubWindowHours: 'Finestra (ore)',
      scrubEvery: 'ogni {days} g, finestra {hours} h',
      scrubOff: 'spento',
      editTitle: 'Modifica la destinazione {name}',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini',
      kind: 'Tipo',
      kinds: {
        local: 'Percorso locale',
        s3: 'S3',
        tape: 'Nastro (copie WORM)'
      },
      kindHints: {
        local:
          'Una directory sul nodo esecutore — per rig e piccole installazioni; i byte sono al sicuro quanto quel filesystem',
        s3: "Qualsiasi object store S3-compatibile (AWS, MinIO, Ceph RGW…) — la destinazione vera, e l'unica che può portare la modalità compliance",
        tape: 'Un drive a nastro su un nodo — una destinazione di COPIE (archivia un backup pronto su una cartuccia WORM), mai di backup'
      },
      kindImmutableHint: 'Il tipo è fissato alla creazione — ritipizzare una destinazione è una NUOVA destinazione',
      path: 'Percorso',
      pathHint: 'Percorso assoluto di una directory, scrivibile su ogni nodo che può eseguire task di backup',
      endpoint: 'URL endpoint',
      endpointHint: 'Sempre esplicito, es. https://minio.example:9000 — nessuna deduzione per provider',
      s3Bucket: 'Bucket',
      s3BucketHint:
        'La modalità compliance richiede Object Lock abilitato sul bucket (fissato alla creazione del bucket S3)',
      region: 'Region',
      regionHint: 'Region di firma opzionale — gli store S3-compatibili spesso la ignorano',
      prefix: 'Prefisso chiavi',
      prefixHint: 'Opzionale — anteposto a ogni oggetto scritto da questa destinazione',
      forcePathStyle: 'Indirizzamento path-style',
      forcePathStyleHint:
        'endpoint/bucket/chiave invece del virtual-hosted — il default amico di MinIO; AWS li accetta entrambi',
      credential: 'Credenziale S3',
      credentialHint:
        "Una voce del keystore di tipo “Account S3” — la chiave segreta è risolta dal worker al momento dell'uso, mai mostrata",
      noCredentials: 'Nessuna credenziale Account S3 nel keystore — creane prima una dalla pagina Credenziali.',
      immutabilityTitle: 'Politica di immutabilità',
      mode: 'Modalità di immutabilità',
      modes: {
        off: 'Off',
        governance: 'Governance',
        compliance: 'Compliance'
      },
      modeHints: {
        off: 'La retention è consultiva: una finestra può comunque essere stampata e mietuta, ma le cancellazioni funzionano in ogni momento',
        governance:
          'TVStor rifiuta la cancellazione prima della fine della finestra — per ogni principal, admin e sudo inclusi; non esiste alcun percorso di forzatura',
        compliance:
          'Governance PIÙ la destinazione stessa applica la medesima finestra (S3 Object Lock, modalità compliance)'
      },
      modeWarning:
        'Confine onesto — la Governance è applicata dalla sola API di TVStor: nessun endpoint, flag o forzatura può eliminare un backup protetto, per nessun principal admin inclusi; chi possiede però le credenziali grezze della destinazione (root sul filesystem, le chiavi S3) può ancora distruggere i byte fuori da TVStor. La Compliance stampa in aggiunta S3 Object Lock con la stessa data, così il rifiuto è della destinazione — anche verso le credenziali storage.',
      futureOnlyRule:
        "Le modifiche di politica parlano solo ai backup FUTURI: ogni backup congela finestra e modalità sulla propria riga alla creazione, quindi spegnere l'immutabilità o abbassare la finestra non indebolisce MAI nulla di già preso.",
      complianceNeedsS3:
        'La compliance richiede una destinazione S3 su un bucket con Object Lock — un filesystem non può fare quella promessa',
      retentionDays: 'Finestra di retention (giorni)',
      retentionDaysHint:
        'Stampata su ogni backup futuro alla creazione: retain-until = momento di creazione + finestra',
      retentionDaysRequired:
        'Governance e compliance richiedono una finestra — una promessa di immutabilità ne ha bisogno',
      retentionDaysOptional:
        'Opzionale con modalità Off: presente = finestra consultiva, assente = i backup vivono finché eliminati a mano',
      backupCount: 'Backup',
      noBackups: 'Ancora nessuno',
      deleteRetainedHint:
        'Una destinazione con backup governance/compliance dentro la finestra non si può rimuovere finché la finestra non passa — è la promessa che funziona come progettato.',
      tsa: {
        label: 'Marcatura temporale (TSA)',
        none: 'Nessuna autorità',
        noneHint:
          'Assenza dichiarata, non una modalità degradata: nessuna autorità RFC 3161 è configurata, quindi i backup futuri portano lo stato “none” — nessuno è stato interpellato e niente è fallito',
        configured: 'TSA configurata',
        verifyEveryShort: 'rilettura ogni {days} g',
        verifyOffShort: 'verifica periodica spenta',
        url: "Endpoint dell'autorità",
        verifyEvery: 'Verifica periodica',
        verifyEveryDays:
          'Ogni {days} giorni il passo ri-scarica token, metadati e bundle LTV (~10 KiB per backup, mai il payload) e ri-esegue la ricetta pubblica di verifica — una marca che marcisce viene retrocessa onestamente sulla sua riga invece di restare un verde che mente',
        verifyOff: "Spenta (0 giorni) — la scelta dichiarata dell'operatore",
        policyOid: 'OID di policy',
        policyDefault: "Default dell'autorità — accettato e registrato su ogni backup",
        hashAlg: "Digest dell'impronta",
        hashAlgs: {
          sha256: 'SHA-256',
          sha384: 'SHA-384',
          sha512: 'SHA-512'
        },
        qualified: 'Qualificata (eIDAS)',
        qualifiedYes:
          "Dichiarata QUALIFICATA dall'operatore — mai dedotta né verificata da TVStor: il prodotto ripete la dichiarazione, non concede la presunzione",
        qualifiedNo:
          'Non dichiarata qualificata — un token RFC 3161 non ha presunzione legale propria: la presunzione eIDAS vale solo per le marche qualificate',
        pin: "Pin TLS dell'endpoint",
        pinNone:
          "Nessuno — l'endpoint è http semplice: in RFC 3161 la sicurezza è la firma del token, non il trasporto, e viaggia solo un hash",
        anchor: 'Àncora di fiducia',
        anchorPresent:
          "PEM fornito ({chars} caratteri) — ciò contro cui i token si verificano qui, e ciò che l'operatore consegna a un terzo insieme al token",
        copyAnchor: 'Copia PEM',
        copiedAnchor: 'Copiato',
        downloadAnchor: 'Scarica PEM',
        declare: "Dichiara un'autorità RFC 3161",
        declareHint:
          "Spento è un'assenza DICHIARATA, non una modalità degradata: i backup futuri portano lo stato “none” — nessuno è stato interpellato e niente è fallito",
        forwardOnly:
          "Come l'immutabilità, questa è una POLITICA e parla solo IN AVANTI: ciò che un backup ha ottenuto è congelato sulla sua riga, quindi puntare la destinazione a un'altra autorità non riscrive mai chi ha firmato un backup preso due anni fa.",
        clearWarning:
          "Rimuovere l'autorità smette di marcare i backup FUTURI (il loro stato leggerà “none”); le righe già marcate conservano il rapporto congelato, e la verifica periodica finisce insieme alla dichiarazione.",
        urlHint:
          "L'endpoint TSP. L'http semplice è la scelta ORDINARIA — in RFC 3161 la sicurezza è la firma del token, non il trasporto, e dal cluster esce solo un hash; con https: il pin TLS qui sotto diventa obbligatorio",
        anchorFieldHint:
          "OBBLIGATORIA, in PEM — deliberatamente NON «il bundle di sistema»: il disegno ha misurato che il bundle non basta e cambia sotto i piedi. È anche l'àncora che l'operatore consegna a un terzo insieme al token",
        anchorNotPem: 'Non è un certificato PEM — incolla il blocco intero, compreso «-----BEGIN CERTIFICATE-----»',
        pinFormHint:
          "sha-256 del certificato dell'endpoint, 64 caratteri esadecimali (i due punti sono tollerati — spedito normalizzato). Obbligatorio con un endpoint https:, rifiutato con http: — un'àncora che non pinna nulla sarebbe peggio dell'assenza onesta di TLS",
        pinRequired:
          "Un endpoint TSA https: non viene mai contattato senza il suo pin — l'ammissione rifiuta nominando questo campo",
        pinForbidden: "Un endpoint http semplice non ha TLS da ancorare — svuota il pin o passa l'URL a https:",
        policyOidHint:
          "Opzionale, inviata con la richiesta: un'autorità che non sa onorarla RIFIUTA (RFC 3161) — il controllo è dell'autorità stessa. Vuota = la policy di default dell'autorità, accettata e registrata su ogni backup",
        hashAlgHint: "Il digest dell'impronta — il campo esiste perché fra dieci anni non sarà sha256",
        verifyEveryLabel: 'Ri-verifica ogni (giorni)',
        verifyEveryFormHint:
          'Il passo periodico rilegge le marche di questa destinazione ogni N giorni (token, metadati e bundle LTV — mai il payload). Default 30 e attiva da sé; 0 la spegne, ed è la tua scelta dichiarata',
        qualifiedDeclare: 'Dichiarata qualificata (eIDAS)',
        qualifiedWarning:
          'Dichiarazione TUA, mai verificata né dedotta da TVStor: la presunzione eIDAS vale solo per una marca qualificata emessa da un QTSP nella EU Trusted List (servizio in stato «granted»). Il prodotto ripete ciò che dichiari — su registrazioni che possono finire in tribunale.'
      },
      tabOverview: 'Panoramica'
    },
    backups: {
      title: 'Backup',
      createTitle: 'Crea backup',
      scrubLabel: 'Byte',
      scrubStates: {
        none: 'mai scrubbato',
        ok: 'byte verificati',
        failed: 'UN BYTE NON TORNA',
        unreadable: 'illeggibile'
      },
      scrubNow: 'Scrub ora',
      scrubbing: 'Rilettura…',
      archiveTarget: 'Destinazione a nastro',
      archiveMedium: 'Etichetta cartuccia',
      archiveRun: 'Archivia su nastro',
      copiesChip: 'WORM ×{count}',
      copiesLabel:
        'Copie WORM (una copia protegge il backup dalla cancellazione; dichiara il supporto perso per sbloccarla)',
      copyPhases: {
        pending: 'in coda',
        writing: 'in scrittura',
        verifying: 'in rilettura',
        ready: 'pronta',
        error: 'errore'
      },
      mediumStates: {
        loaded: 'nel drive',
        ejected: 'espulsa',
        unknown: 'non osservata',
        lost: 'PERSA'
      },
      volume: 'Volume',
      volumeHint: 'I backup si prendono da uno snapshot di questo volume — mai dalla testa viva',
      volumeGone: 'Volume eliminato',
      volumeGoneHint: 'Il volume sorgente non esiste più — il backup resta ripristinabile in un volume nuovo',
      snapshot: 'Snapshot',
      snapshotAuto: 'Prendine uno ora (consigliato)',
      snapshotHint:
        'Lasciato in automatico, la piattaforma prende prima uno snapshot (task figlio) e lo tiene come base del diff per il prossimo incrementale della catena volume+destinazione',
      target: 'Destinazione',
      targetHint:
        'La destinazione e la sua politica di immutabilità — la finestra viene stampata e congelata alla creazione',
      kind: 'Tipo',
      kinds: {
        auto: 'Automatico',
        full: 'Completo',
        incremental: 'Incrementale'
      },
      kindHint:
        'Automatico sceglie incrementale quando esiste una base usabile (il più recente backup pronto di questo volume su questa destinazione), completo altrimenti; un incrementale spedisce solo gli extent cambiati fra lo snapshot base e quello nuovo',
      phase: 'Fase',
      phases: {
        pending: 'In attesa',
        running: 'In corso',
        ready: 'Pronto',
        error: 'Errore',
        expiring: 'In scadenza'
      },
      timestampLabel: 'Marca temporale',
      timestampStates: {
        none: 'Nessuna autorità',
        ok: 'Marcato',
        unverifiable: 'NON marcato — non verificabile',
        unreachable: 'NON marcato — autorità irraggiungibile',
        refused: 'NON marcato — rifiutata'
      },
      timestampNotQualified: 'non qualificata eIDAS',
      timestampTab: {
        title: 'Marca temporale',
        empty: 'Nessun rapporto di marca su questa riga.',
        claim: 'La dichiarazione',
        claimHint:
          "L'unica frase che il prodotto è autorizzato a dire — coniata lato server in un punto solo, così webui, CLI e rapporto di verifica non possono divergere",
        state: 'Stato',
        authority: 'Autorità',
        authorityHint:
          "L'endpoint che ha marcato, o che è stato interpellato — copia congelata della configurazione della destinazione al momento della spedizione",
        stampedAt: 'Ora dentro il token',
        stampedAtHint: "L'ora dell'autorità, mai l'orologio di questo cluster — la data opponibile a un terzo",
        noToken: 'Nessun token',
        policy: 'Policy TSA',
        reason: 'Motivo',
        ltv: 'Bundle LTV',
        ltvStates: {
          none: 'Assente',
          partial: 'Parziale',
          complete: 'Completo',
          retrofitted: 'Retrofit'
        },
        ltvHints: {
          none: 'Ancora nessun bundle accanto a questo token — vero per i backup marcati prima della funzione LTV, finché il passo periodico non li retrofitta',
          partial:
            'Il bundle esiste ma manca una CRL (un distribution point non ha risposto) — il motivo dice quale e perché',
          complete:
            'Àncora più una CRL per ogni distribution point dichiarato, congelate al momento della marca: la marca si ri-verifica OFFLINE, a qualsiasi data futura, con un comando openssl standard e senza TVStor',
          retrofitted:
            'Costruito DOPO la marca con le CRL di oggi: prova la non-revoca alla data del retrofit, non a quella della marca — meno di completo, e lo dice invece di fingere'
        },
        checkedAt: 'Ultima rilettura',
        checkedAtNever:
          'Mai dalla marca — vero per ogni backup che precede la funzione e per ogni backup non ancora in scadenza di verifica',
        checkedAtHint:
          "Quando il passo periodico (o l'azione di verifica) ha riletto per l'ultima volta questa marca — avanza a ogni visita, verde o rossa; ciò che ha trovato sta in stato e motivo",
        observedAt: 'Rapporto scritto il',
        observedAtHint:
          "Col nostro orologio — nominato a parte dall'ora del token proprio perché non è la data che conta",
        offlineRecipe:
          "Il token vive accanto al backup sulla destinazione (tst.tsr, insieme a meta.json e data) ed è verificabile SENZA TVStor da chiunque abbia l'àncora: openssl ts -verify -data meta.json -in tst.tsr -CAfile <àncora>. Cosa prova: che esattamente questo contenuto esisteva NON PIÙ TARDI di quella data. Cosa non prova: che i byte siano ancora là.",
        cadence: 'Verifica periodica (politica della destinazione)',
        cadenceEvery:
          'Ogni {days} giorni — la politica ATTUALE della destinazione: dice quando il passo rilegge, mai cosa ha ottenuto questa riga',
        cadenceOff: "Spenta (0 giorni) — la scelta dichiarata dell'operatore sulla destinazione",
        cadenceNoAuthority:
          'La destinazione oggi non dichiara alcuna autorità — le righe già marcate conservano il loro rapporto congelato',
        cadenceUnknown: 'Destinazione non leggibile — cadenza sconosciuta',
        verifyAction: 'Verifica ora',
        verifyHint:
          "Ri-scarica token e metadati dalla destinazione (pochi KiB — mai il payload) e ri-esegue la ricetta pubblica di verifica contro l'àncora congelata su questa riga. Una marca che non verifica più viene retrocessa onestamente sulla riga — è il punto, non un danno",
        verifyBlockedNoToken:
          'Niente da rileggere: questa riga non porta alcun token (autorità assente, irraggiungibile o che ha rifiutato alla spedizione) — anche il server rifiuta, e inventare la verifica del nulla sarebbe peggio',
        verifyRunning: 'Task di verifica in corso — la destinazione viene riletta…',
        verifyFailed: 'La verifica è FALLITA — le parole del server:',
        verifyReread:
          'Rapporto riletto: la pagina qui sotto è il verdetto fresco della riga, non una deduzione del client.'
      },
      snapshotProvenanceHint:
        'Provenienza: lo snapshot da cui è stata presa questa copia, tenuto come base della catena — eliminarlo forza soltanto il prossimo backup di questa catena a essere completo',
      snapshotNotRecorded: 'Non ancora registrato — lo registra il task figlio della piattaforma',
      dependents: 'Dipendenti',
      dependentsNone: 'Nessun incrementale dipende da questo backup',
      dependentsSome:
        '{count} incrementali indicano questo backup come base — la cancellazione rifiuta (409 in_use) finché non spariscono: elimina prima le foglie',
      chain: {
        tabTitle: 'Catena',
        hint: 'La catena intera a cui questo backup appartiene, composta dalle righe caricate: il full alla radice, gli incrementali rientrati sotto la loro base. Il ripristino materializza il percorso dalla radice alla riga scelta; la cancellazione va foglia-per-prima (il server rifiuta una base in uso).',
        thisRow: 'questo backup',
        missingBase:
          "La base {id} di questa catena non è tra le righe caricate — l'albero qui sotto parte dall'antenato noto più vicino."
      },
      size: 'Dimensione',
      retainUntil: 'Conserva fino al',
      retention: {
        unprotected: 'Senza finestra',
        advisory: 'Consultiva',
        locked: 'Bloccato',
        expired: 'Scaduta'
      },
      retentionHints: {
        unprotected: 'Nessuna finestra di retention su questa riga — vive finché eliminata a mano',
        advisory:
          'La finestra è consultiva (modalità Off congelata alla creazione) — lo sweeper miete oltre la scadenza, ma la cancellazione funziona già oggi',
        locked:
          'Governance/compliance congelata dentro la finestra: la cancellazione è rifiutata a ogni principal finché la data non passa',
        expired: 'La finestra è passata — la cancellazione funziona e lo sweeper di scadenza mieterà la riga'
      },
      immutabilityMode: 'Immutabilità',
      frozenHint:
        'Copia congelata della politica della destinazione alla creazione — questa riga si giudica contro di essa, mai contro la politica attuale della destinazione',
      base: 'Base',
      baseFull: 'Completo — senza base',
      noVolumes: 'Ancora nessun volume di cui fare backup.',
      noTargets: 'Ancora nessuna destinazione backup — creane prima una dalla pagina Destinazioni backup.',
      noSnapshots: "Nessuno snapshot pronto su questo volume — lascia l'automatico per prenderne uno ora.",
      createFollowHint:
        'La retention è stampata prima che si muova il primo byte — il backup è protetto dal momento in cui esiste.',
      restore: {
        action: 'Ripristina',
        title: 'Ripristina il backup di {volume}',
        chainHint:
          "Il ripristino materializza l'intera catena — il completo, poi ogni incrementale fino a questo backup, in ordine — letta dagli oggetti sulla destinazione: funziona anche con il volume sorgente sparito da tempo.",
        variant: 'Ripristina in',
        variantExisting: 'Sovrascrivi un volume esistente',
        variantNew: 'Un volume nuovo',
        targetVolume: 'Volume da sovrascrivere',
        targetVolumeHint: 'Deve essere pronto o degradato, grande almeno quanto il backup, e non esportato',
        overwriteWarning:
          "Distruttivo: il contenuto del volume viene sostituito con l'immagine ripristinata. I suoi snapshot sopravvivono come punti di revert.",
        confirmLabel: 'Digita il nome del volume per confermare',
        confirmHint: 'Digita “{name}” esattamente — il suo contenuto sta per essere sostituito',
        newName: 'Nome del nuovo volume',
        newNameHint: 'Minuscole, cifre e trattini',
        datapool: 'Cluster',
        datapoolHint:
          'Dove nasce il nuovo volume — predefinito al cluster del volume sorgente finché quel volume esiste',
        datapoolDefault: 'Cluster del volume sorgente (predefinito)',
        notReady: 'Solo un backup pronto si può ripristinare',
        submit: 'Ripristina'
      },
      deleteTitle: 'Eliminare il backup?',
      deleteWarning: "Il task di eliminazione rimuove l'oggetto dalla destinazione ed elimina la riga.",
      deleteLocked:
        "Questo backup è IMMUTABILE fino al {date}: la sua finestra {mode} congelata rifiuta la cancellazione a ogni principal — admin e sudo inclusi. Non esiste alcun flag di forzatura, e disattivare l'immutabilità sulla destinazione vale solo per i backup futuri.",
      deleteChainHint:
        'Un backup referenziato da un incrementale rifiuta la cancellazione (409 in_use) — elimina prima le foglie.',
      deleteErrorFreely:
        'Una copia fallita non protegge nulla — questa riga si elimina liberamente a prescindere dalla retention.',
      tabOverview: 'Panoramica'
    },
    replication: {
      title: 'Link di replica',
      createTitle: 'Crea link di replica',
      editTitle: 'Modifica il link {name}',
      name: 'Nome',
      nameHint: 'Minuscole, cifre e trattini',
      remoteTitle: 'Cluster remoto',
      remoteUrl: 'URL del backend',
      remoteUrlHint: 'Qualsiasi nodo membro del cluster di destinazione risponde, es. https://node1.dr.example:8443',
      clusterName: 'Nome cluster atteso',
      clusterNameHint:
        "Verificato contro la risposta dell'endpoint di ricezione a ogni sync — i dati non partono mai verso un cluster che risponde con un'identità diversa",
      credential: 'Token di replica',
      credentialHint:
        "Una voce del keystore di tipo “Token di replica”, coniata dall'operatore della DESTINAZIONE — presentata come bearer al piano interno di ricezione del remoto, mai una sessione utente",
      noCredentials:
        "Nessun token di replica nel keystore — lo conia l'operatore della destinazione; registralo prima dalla pagina Credenziali.",
      certFingerprint: 'Impronta del certificato della destinazione (àncora TLS)',
      certFingerprintHint:
        "sha-256 del certificato TLS del backend di destinazione, 64 caratteri esadecimali (i due punti sono tollerati) — l'operatore della destinazione la legge accanto al token tvsl_. Ignorata su un URL http semplice.",
      anchorRequired:
        "Una destinazione https non viene mai contattata senza l'impronta del suo certificato — ogni sync rifiuterebbe nominando questo campo. Incolla l'àncora che l'operatore della destinazione ti ha consegnato col token.",
      trustBoundary:
        'Il token autorizza esattamente una cosa sulla destinazione: spingere delta stream nei suoi volumi replica. Non è un utente e non porta altri permessi.',
      volumes: 'Volumi',
      volumesHint:
        'Aggiungere un volume parte con un seed completo al prossimo sync; rimuoverlo ferma le spedizioni ma non tocca mai la destinazione — la sua replica resta.',
      volumesEmpty: 'Nessun volume eleggibile — i volumi replica non si possono replicare a loro volta.',
      volumeCount: '{count} volumi',
      schedule: 'Pianificazione',
      scheduleManual: 'Solo manuale (Sincronizza ora)',
      everyMinutes: 'Sincronizza ogni (minuti)',
      everyMinutesHint: 'Un sync lento si auto-limita: due sync sovrapposti dello stesso link non accadono mai',
      enabled: 'Attivo',
      enabledHint:
        'La pausa conserva watermark e repliche remote; la riattivazione riprende incrementale dalla base tenuta',
      pausedChip: 'In pausa',
      phase: 'Fase',
      phases: {
        idle: 'Inattivo',
        syncing: 'In sync',
        error: 'Errore',
        promoted: 'Promosso'
      },
      lastSyncedAt: 'Ultimo sync',
      neverSynced: 'Mai',
      directionLabel: 'Direzione',
      directions: {
        outbound: 'In uscita'
      },
      directionHint:
        "Un link vive sul cluster SORGENTE e spinge. La destinazione non ha un oggetto link — la sua metà è l'endpoint di ricezione autenticato dal token più i volumi replica in sola lettura che materializza.",
      lag: 'Lag',
      lagHint:
        'Secondi dal sync per-volume più vecchio — il limite superiore osservato della perdita dati se il sito sorgente morisse ora (RPO osservato, non promesso)',
      remoteReadOnly:
        'Le repliche remote sono in sola lettura: rifiutano export, scritture e revert. Promuovere una copia DR a primaria è un round successivo — eliminare un link non le tocca mai.',
      syncNow: {
        action: 'Sincronizza ora',
        title: 'Sincronizza {name} ora',
        hint: 'Esegue lo stesso ciclo di sync della pianificazione, fuori orario — es. subito prima di una manutenzione pianificata del sito sorgente. Un figlio per volume: snapshot, diff, spedizione, commit.',
        blockedDisabled: 'Il link è in pausa — riattivalo prima',
        blockedSyncing: 'Un sync è già in corso — mai due sync concorrenti dello stesso link',
        submit: 'Sincronizza ora'
      },
      volumeStates: {
        tabTitle: 'Volumi',
        empty: 'Ancora nessun watermark per-volume — il primo sync li crea.',
        volume: 'Volume',
        baseSnapshot: 'Snapshot base',
        noBase: 'Seed completo al prossimo sync',
        lastSyncedAt: 'Ultimo sync',
        lastError: 'Ultimo errore',
        ok: 'OK'
      },
      deleteWarning:
        'Eliminare il link annulla un sync in corso e raccoglie gli snapshot di replica locali — la destinazione non viene mai toccata: le sue repliche restano.',
      tabOverview: 'Panoramica',

      // ENEA-A-R9 «replica file» (2026-10-23): la forma FILE del link.
      kind: 'Forma',
      kinds: {
        block: 'Blocchi',
        file: 'File (mirror CephFS)'
      },
      kindHint:
        'Blocchi: i volumi spediscono delta stream. File: le share CephFS sono specchiate da cephfs-mirror, snapshot per snapshot — si decide alla nascita, immutabile.',
      fileFormHint:
        'UN PEER PER FILESYSTEM (la stella): ogni share di un filesystem CephFS viaggia sullo stesso link — un secondo link su un filesystem già specchiato è rifiutato. Mon, MDS e OSD del peer devono essere raggiungibili dal nodo di controllo ceph di questo cluster, ed ENTRAMBI i cluster devono parlare msgr2 secure (altrimenti il bind rifiuta, parlando). Il peering stesso viaggia col token e l’àncora TLS di questo link.',
      members: 'Membri',
      shares: 'Share',
      sharesHint:
        'Share CephFS, ready, non repliche a loro volta. Aggiungerne una registra il suo subvolume al mirror (la prossima cadenza ne scatta il primo snapshot — una copia completa sul peer); toglierla la deregistra e lascia la share replica del peer com’è.',
      sharesEmpty: 'Nessuna share eleggibile — un link file specchia share CephFS ready che non sono repliche.',
      shareCount: '{count} share',
      cadence: 'Cadenza degli snapshot',
      fileCadenceHint:
        'Uno snapshot di piattaforma per share a ogni tick; il demone spedisce ogni snapshot che trova. Vuoto = solo manuale (Sincronizza ora).',
      retainSnapshots: 'Snapshot da trattenere',
      retainSnapshotsHint:
        'Quanti snapshot di cadenza restano su ogni share — e, poiché cephfs-mirror propaga le cancellazioni, sul peer. Il più recente e l’ultimo sincronizzato non si potano mai.',
      retainSnapshotsDetail:
        'Retention: {count} snapshot di cadenza per share (il peer tiene gli stessi — le cancellazioni propagano).',
      whatTravels:
        'Cosa arriva sul peer: i DATI degli snapshot (e gli snapshot Ceph nudi, anche quelli dell’utente). Cosa NON arriva: le risorse snapshot, i loro nomi e la retention congelata, la quota (riapplicata alla promozione). La promozione è un verbo sulla share di DESTINAZIONE; nessun failback automatico — la protezione inversa è un link nuovo dal cluster promosso.',
      mirrorTitle: 'Mirror',
      peerPhases: {
        pending: 'in attesa',
        bound: 'legato',
        error: 'errore',
        unbinding: 'in uscita'
      },
      daemonUp: 'demone su, su {node}',
      daemonDown: 'demone GIÙ su {node}',
      daemonUnknown: 'demone non ancora osservato',
      shareStates: {
        tabTitle: 'Share',
        blockLink: 'Un link a blocchi non ha stati per share — vedi la scheda Volumi.',
        empty: 'Nessuno stato per share — il nodo di controllo osserva il demone del mirror al prossimo tick.',
        share: 'Share',
        state: 'Stato',
        states: {
          unknown: 'Sconosciuto',
          idle: 'Fermo',
          syncing: 'In sincronizzazione',
          failed: 'Fallito'
        },
        lastSnapshot: 'Ultimo snapshot',
        lastSyncedSnapshot: 'Ultimo sincronizzato',
        lag: 'RPO',
        failureReason: 'Guasto'
      }
    }
  }
}
